import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Button } from "@components/ui/button";
import { Progress } from "@components/ui/progress";
import { File, X } from "lucide-react";
import { ReportSavedDataItem } from "./CommonTypes";
import Confirmation from "@components/common/ConfirmDialog";
import { toast } from "@components/ui/use-toast";
import {
  deleteReport,
  downloadSavedReport,
  fetchSavedReportData,
} from "@services/api/aiPoweredEngine";
import PageLoader from "@components/loaders/PageLoader";
import { GeneralContext } from "@services/providers/GeneralProvider";
import { useQuery } from "react-query";

interface ReportStatusPopupProps {
  statusDialog: boolean;
  handleCloseStatusDialog: () => void;
}

const ReportStatusPopup: React.FC<ReportStatusPopupProps> = ({
  statusDialog,
  handleCloseStatusDialog,
}) => {
  const [enrichedReports, setEnrichedReports] = useState<any[]>([]);
  const [selectedReport, setSelectedReport] =
    useState<ReportSavedDataItem | null>(null);
  const [trashOpened, setTrashOpened] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const { data: reportsaveddata } = useQuery(
    ["fetchSavedReportData"],
    ({ signal }) => fetchSavedReportData(signal),
    {
      refetchInterval: 10000,
    },
  );

  const text: string = "Are you sure you want to remove this?";

  const { productType } = React.useContext(GeneralContext);

  // AnimatedProgress Component
  const AnimatedProgress: React.FC<{ target?: number }> = ({ target = 90 }) => {
    const [progress, setProgress] = useState(0);

    useEffect(() => {
      let current = 0;

      const animate = () => {
        if (current < 50) {
          current += 2;
          setProgress(current);
          setTimeout(animate, 100);
        } else if (current < target) {
          current += 1;
          setProgress(current);
          setTimeout(animate, 200);
        }
      };
      animate();
      return () => {
        setProgress(0);
      };
    }, [target]);

    return (
      <div className="w-full border p-3 rounded-lg">
        <Progress
          value={progress}
          className="bg-secondary transition-all duration-300"
        />
      </div>
    );
  };

  const enrichReportData = useCallback(() => {
    if (!reportsaveddata?.length) return;

    const reportsArray = Array.isArray(reportsaveddata) ? reportsaveddata : [];

    const enriched = reportsArray.map((item) => {
      const status = item?.status || "progress";

      const reportName =
        item.report_name === "ThreatShadow" && productType === "ddr"
          ? "Risk and Compliance Report"
          : item.report_name === "ThreatShadow" &&
              (productType === "ndr" || productType === "itdr")
            ? "AML Anomaly Report"
            : item.report_name === "Custom Dashboard"
              ? item.DashboardName || "Custom Dashboard"
              : item.report_name === "Identity Intelligence"
                ? Array.isArray(item.identityIntelligenceWidgets) &&
                  item.identityIntelligenceWidgets.length > 0
                  ? `Identity Intelligence - ${item.identityIntelligenceWidgets.join(", ")}`
                  : typeof item.identityIntelligenceWidgets === "string" &&
                      item.identityIntelligenceWidgets.trim() !== ""
                    ? `Identity Intelligence - ${item.identityIntelligenceWidgets}`
                    : "Identity Intelligence"
                : item.report_name;

      return {
        ...item,
        status,
        report_name: reportName,
        report_type: item.report_type,
        time: item.time,
      };
    });

    const sorted = enriched.sort((a, b) => {
      const parseDate = (str: string) => {
        const [day, month, yearAndTime] = str.split("-");
        const [year, time] = yearAndTime.split(" ");
        return new Date(`${year}-${month}-${day}T${time}`);
      };

      return parseDate(b.time).getTime() - parseDate(a.time).getTime();
    });

    setEnrichedReports(sorted);
  }, [reportsaveddata]);

  useEffect(() => {
    enrichReportData();
  }, [reportsaveddata]);

  const handleTrashPopupOpen = (report: ReportSavedDataItem) => {
    setSelectedReport(report);
    setTrashOpened(true);
  };

  const onDelete = async () => {
    if (!selectedReport) return;
    try {
      setLoading(true);
      // Perform API call to delete the report
      const report_type = selectedReport?.report_type;
      const created_on = selectedReport?.created_on;
      const uqid = selectedReport.uqid;
      const download_type = selectedReport.download_type;
      const save_type = selectedReport.save_type ?? "";

      const response = await deleteReport(
        report_type,
        created_on,
        uqid,
        download_type,
        save_type,
      );

      if (response.message === "Success") {
        toast({
          title: "Success",
          description: "Report successfully deleted.",
          variant: "success",
          duration: 2000,
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to delete report.",
          variant: "destructive",
          duration: 2000,
        });
      }
    } catch (error) {
      console.error("Error deleting report:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while deleting.",
        variant: "destructive",
        duration: 2000,
      });
    } finally {
      setLoading(false);
      setTrashOpened(false);
      setSelectedReport(null);
    }
  };

  const onClose: () => void = () => {
    setTrashOpened(false);
  };

  const handleDownload = async (report: ReportSavedDataItem) => {
    setLoading(true);

    const downloadUrl = `/download/report/?type=${report.report_type}&createdOn=${report.created_on}&uqid=${report.uqid}&download_type=${report.download_type}&customDashboardName=${report.customDashboardName}&fileName=${report.fileName}`;

    try {
      await downloadSavedReport({
        url: downloadUrl,
        fileName: report.fileName,
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to download the report.",
        variant: "destructive",
        duration: 2000,
      });
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <PageLoader show={loading} />

      {trashOpened && (
        <Confirmation
          text={text}
          confirmAction={onDelete}
          rejectAction={onClose}
          confirmText="Yes, delete it!"
          rejectText="Cancel"
          show={trashOpened}
          closeDialog={onClose}
        />
      )}
      <Dialog open={statusDialog} onOpenChange={handleCloseStatusDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader className="border-b pb-4">
            <DialogTitle>Saved Reports</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4 max-h-[400px] overflow-y-auto">
            {enrichedReports.map((report, index) => {
              return (
                <div
                  key={report.uqid || index}
                  className="flex gap-4 items-start border p-3 rounded-lg shadow-sm cursor-pointer"
                >
                  <div className="flex items-center justify-center w-12 h-12">
                    <div
                      className="mt-1 border rounded-full p-2 bg-primary"
                      onClick={() => handleDownload(report)}
                    >
                      <File size={24} />
                    </div>
                  </div>
                  <div className="flex flex-row items-start gap-4 w-full">
                    <div
                      className="flex flex-col gap-1 w-full"
                      onClick={() => handleDownload(report)}
                    >
                      <h2 className="text-md font-semibold capitalize">
                        {report.report_name || "Unknown Report"}
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        Created on: {report.time}
                      </p>
                      {report.status === "progress" && (
                        <AnimatedProgress target={90} />
                      )}

                      {report.status === "completed" && (
                        <span className="text-green-600 text-sm">
                          Completed
                        </span>
                      )}

                      {report.status === "failed" && (
                        <span className="text-red-600 text-sm">Failed</span>
                      )}
                    </div>

                    <div className="ml-auto cursor-pointer">
                      <X
                        size={16}
                        onClick={() => handleTrashPopupOpen(report)}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button onClick={handleCloseStatusDialog}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportStatusPopup;
